import Application from "../../Application";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IOrganisation } from "../../organisations/model/Organisation";
import { ICourtCase } from "../model/CourtCase";
import { CaseLawDocumentStatus, CourtCaseType } from "../common/Constants";
import { ICaseLawDocument } from "../model/CaseLawDocument";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import CreateCaseLawDocumentInteractor from "../interactor/CreateCaseLawDocumentInteractor";
import FindCourtCaseDetailsInteractor from "../interactor/FindCourtCaseDetailsInteractor";
import GetAllOrganisationAttachmentTypesInteractor from "../../organisations/interactors/GetAllOrganisationAttachmentTypesInteractor";
import GetCaseLawDocumentTypesForOrganisationInteractor from "../../organisations/interactors/GetCaseLawDocumentTypesForOrganisationInteractor";
import { IUser } from "../../user/model/User";
import FindIndexesInCategoryForOrganisationInteractor from "../../organisations/interactors/FindIndexesInCategoryForOrganisationInteractor";
import { TSearchQueryRequest } from "../../search/service/SearchService";
import RunSimpleSearchQueryInteractor from "../../search/interactor/RunSimpleSearchQueryInteractor";
import { IInvalidatedCaseLawDocument } from "../model/InvalidatedCaseLawDocument";
import SummaryTranslation from "../../core/model/translations/SummaryTranslation";
import OrganisationIndexPresentationModel from "../../organisations/presenter/model/OrganisationIndexPresentationModel";
import { TOrganisationIndexGroupPresentationModel } from "../../organisations/presenter/model/OrganisationIndexGroupPresentationModel";
import { IDocumentType } from "../../documentTypes/model/DocumentType";
import { IDocumentTypeIndexCategory } from "../../documentTypes/model/DocumentTypeIndexCategory";
import { ICaseDecision } from "../../caseDecisions/model/CaseDecision";
import { ICourtCaseConnectionType } from "../model/CourtCaseConnectionType";
import CreateAttachmentFileInteractor from "../interactor/attachments/CreateAttachmentFileInteractor";
import { IAttachment } from "../model/Attachment";
import ShowInfoMessageInteractor from "../../core/interactor/notifications/ShowInfoMessageInteractor";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import { TSearchCriteriaFilter } from "../../search/model/SearchCriteria";
import GetAllIndexTypeAttributesInteractor from "../../indexTypes/interactors/GetAllIndexTypeAttributesInteractor";
import { IIndexTypeAttribute } from "../../indexTypes/model/IndexTypeAttribute";
import IndexAttributeData, {
  IIndexAttributeData
} from "../../indexTypes/model/IndexAttributeData";
import CreateIndexAttributeDataInteractor from "../../indexTypes/interactors/CreateIndexAttributeDataInteractor";
import GetIndexAttributeDataByIndexInteractor from "../../indexTypes/interactors/GetIndexAttributeDataByIndexInteractor";
import { IOrganisationIndex } from "../../organisations/model/OrganisationIndex";
import ShowWarningMessageInteractor from "../../core/interactor/notifications/ShowWarningMessageInteractor";

export interface TCourtCaseSelectionModel {
  courtCase: ICourtCase | undefined;
  caseDecision: ICaseDecision | undefined;
  relatedCases: {
    courtCase: ICourtCase;
    caseDecision: ICaseDecision | undefined;
  }[];
}
export interface TCaseLawDocumentCreationPresenter
  extends TLoadingAwarePresenter {
  organisations: IOrganisation[];
  selectedOrganisationId: number;
  caseLawDocumentTypes: IDocumentType[];
  organisationIndexes: IDocumentTypeIndexCategory[];
  caseLawDocumentTypeId?: number;
  selectedCourtCases: ICourtCase[];
  selectedIndex: any;
  selectedIndexForAttribute?: IOrganisationIndex;
  connectedCourtCasesList: ICourtCase[];
  courtCase?: ICourtCase;
  user?: IUser;
  parentGroupList: TOrganisationIndexGroupPresentationModel[][];
  secondGroupList: TOrganisationIndexGroupPresentationModel[][];
  selectedParentGroupId: number[];
  selectedSecondGroupId: number[];
  publishedCaseLawDocuments: any;
  searchQuery?: TSearchQueryRequest;
  invalidatePublishedCaseLawDocsVisible: boolean;
  courtCaseDetailsModalVisible?: boolean;
  courtCaseSelectionResult?: TCourtCaseSelectionModel;
  courtCaseConnectionTypeList: ICourtCaseConnectionType[];
  createCourtCasePopupVisible: boolean;
  createCourtCaseModalVisible: boolean;
  nonExistantCourtCaseNumber?: string;
  uploadedAttachment?: IAttachment;
  courtCaseTypes: IGenericCodebookItem[];
  decisionTypes: IGenericCodebookItem[];
  indexTypeAttributes?: IIndexTypeAttribute[];
  indexAttributeDataList?: IIndexAttributeData[];
  indexDataModalVisible: boolean;
  previousSelectedIds: number[][];
}
export interface ICaseLawDocumentCreationPresenter
  extends TCaseLawDocumentCreationPresenter,
    TPresentable {
  loadReferenceValues(
    organisations: IOrganisation[],
    courtCaseConnectionTypeList: ICourtCaseConnectionType[],
    courtCaseTypes: IGenericCodebookItem[],
    decisionTypes: IGenericCodebookItem[]
  ): void;
  setOrganisationId(organisationId: number): void;
  onSearch(value: string): void;
  createCaseLawDocument(caseLawDocument: ICaseLawDocument): void;
  showCaseLawDocumentList(): void;
  createConnectedCourtCase(courtCase: ICourtCase): void;
  removeConnectedCourtCase(courtCaseNumber: string): void;
  findCourtCaseDetails(courtCaseNumber: string): Promise<boolean>;
  setCaseLawDocumentTypeCode(caseLawDocumentTypeId: number): void;
  setParentGroupId(selectedParentGroupId: number, index: number): void;
  setSecondGroupId(selectedSecondGroupId: number, index: number): void;
  setInvalidatePublishedCaseLawDocuments(value: boolean): void;
  updateCourtCaseDetailsModalVisibility(state: boolean): void;
  updateCourtCaseSelection(result: TCourtCaseSelectionModel): void;
  showCreateCourtCasePopupVisible(value: boolean): void;
  showCreateCourtCaseModalVisible(value: boolean): void;
  createCourtCase(courtCase: ICourtCase): void;
  createAttachment(file: any, fileName?: string): any;
  openIndexDataModal(
    index: IOrganisationIndex,
    selectedIds: number[],
    indexNr: number
  ): void;
  closeIndexDataModal(): void;
  setAttributesData(
    indexTypeAttribute: IIndexTypeAttribute,
    attributeData: string
  ): void;
  saveAttributeData(): void;
}
const defaultState: TCaseLawDocumentCreationPresenter = {
  previousSelectedIds: [],
  organisations: [],
  selectedOrganisationId: NaN,
  caseLawDocumentTypes: [],
  organisationIndexes: [],
  selectedCourtCases: [],
  selectedIndex: {},
  connectedCourtCasesList: [],
  courtCase: undefined,
  parentGroupList: [],
  secondGroupList: [],
  selectedParentGroupId: [],
  selectedSecondGroupId: [],
  publishedCaseLawDocuments: {},
  invalidatePublishedCaseLawDocsVisible: false,
  courtCaseConnectionTypeList: [],
  createCourtCasePopupVisible: false,
  createCourtCaseModalVisible: false,
  courtCaseTypes: [],
  decisionTypes: [],
  indexAttributeDataList: [],
  indexDataModalVisible: false
};
const CaseLawDocumentCreationPresenter = withStore<
  ICaseLawDocumentCreationPresenter,
  TCaseLawDocumentCreationPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): ICaseLawDocumentCreationPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TCaseLawDocumentCreationPresenter>();

    const loadReferenceValues = (
      organisations: IOrganisation[],
      courtCaseConnectionTypeList: ICourtCaseConnectionType[],
      courtCaseTypes: IGenericCodebookItem[],
      decisionTypes: IGenericCodebookItem[]
    ) => {
      const user = _store.getState<any>().user;
      if (user && user.isOperator()) {
        setOrganisationId(user.organisationId);
      }
      _store.update({
        organisations,
        user,
        courtCaseConnectionTypeList,
        courtCaseTypes,
        decisionTypes
      });
    };
    const getCaseLawDocumentTypeIndexes = async (
      caseLawDocumentTypeId: number
    ) => {
      loader.start("caseLawDocumentIndexDataLoader");
      const { selectedLanguage, selectedOrganisationId } =
        _store.getState<any>();
      let organisationIndexes = await _application.container
        .resolve<FindIndexesInCategoryForOrganisationInteractor>(
          "findIndexesInCategoryForOrganisation"
        )
        .execute(caseLawDocumentTypeId, selectedOrganisationId);

      let parentGroupList: TOrganisationIndexGroupPresentationModel[][] = [];
      let secondGroupList: TOrganisationIndexGroupPresentationModel[][] = [];

      organisationIndexes.forEach((item) => {
        let parentList: TOrganisationIndexGroupPresentationModel[] = [];
        let secondList: TOrganisationIndexGroupPresentationModel[] = [];
        let indexList = item.organisationIndexList?.map((element) =>
          OrganisationIndexPresentationModel(
            element,
            undefined,
            undefined,
            undefined,
            undefined,
            selectedLanguage
          )
        );
        indexList?.forEach((index) => {
          var parentGroup = index.groups?.find(
            (group) => group.parentId == null
          );
          var secondGroup = index.groups?.find(
            (group) => group.parentId === parentGroup?.id
          );
          if (
            parentGroup &&
            parentList.findIndex(
              (element) => element.id === parentGroup?.id
            ) === -1
          ) {
            parentList?.push(parentGroup);
          }

          if (
            secondGroup &&
            secondList.findIndex(
              (element) => element.id === secondGroup?.id
            ) === -1
          ) {
            secondList.push(secondGroup);
          }
        });
        parentGroupList.push(parentList);
        secondGroupList.push(secondList);
      });
      _store.update({
        organisationIndexes,
        parentGroupList,
        secondGroupList
      });
      loader.stop("caseLawDocumentIndexDataLoader");
    };
    const getAllOrganisationAttachmentTypes = async (
      organisationId: number
    ) => {
      let attachmentTypes = await _application.container
        .resolve<GetAllOrganisationAttachmentTypesInteractor>(
          "getAllOrganisationAttachmentTypes"
        )
        .execute(organisationId);
      _store.update({
        attachmentTypes
      });
    };
    const getOrganisationCaseLawDocumentTypes = async (
      organisationId: number
    ) => {
      loader.start("caseLawDocumentLoader");
      let caseLawDocumentTypes = await _application.container
        .resolve<GetCaseLawDocumentTypesForOrganisationInteractor>(
          "getCaseLawDocumentTypesForOrganisation"
        )
        .execute(organisationId);
      _store.update({
        caseLawDocumentTypes
      });
      if (caseLawDocumentTypes.length > 0) {
        setCaseLawDocumentTypeCode(caseLawDocumentTypes[0].id);
      }
      loader.stop("caseLawDocumentLoader");
    };
    const setCaseLawDocumentTypeCode = async (
      caseLawDocumentTypeId: number
    ) => {
      _store.update({
        caseLawDocumentTypeId: caseLawDocumentTypeId
      });
      return getCaseLawDocumentTypeIndexes(caseLawDocumentTypeId);
    };
    const setParentGroupId = async (selectedGroup: number, index: number) => {
      let { selectedParentGroupId } =
        _store.getState<TCaseLawDocumentCreationPresenter>();
      selectedParentGroupId[index] = selectedGroup;
      _store.update({
        selectedParentGroupId
      });
    };
    const setSecondGroupId = async (selectedGroup: number, index: number) => {
      let { selectedSecondGroupId } =
        _store.getState<TCaseLawDocumentCreationPresenter>();
      selectedSecondGroupId[index] = selectedGroup;
      _store.update({
        selectedSecondGroupId
      });
    };

    const setInvalidatePublishedCaseLawDocuments = async (value: boolean) => {
      _store.update({ invalidatePublishedCaseLawDocsVisible: value });
    };
    const setOrganisationId = async (organisationId: number) => {
      _store.update({ selectedOrganisationId: organisationId });
      await getAllOrganisationAttachmentTypes(organisationId);
      await getOrganisationCaseLawDocumentTypes(organisationId);
    };
    const findCourtCaseDetails = async (courtCaseNumber: string) => {
      let { courtCaseSelectionResult } =
        store.getState<TCaseLawDocumentCreationPresenter>();
      if (!courtCaseNumber || courtCaseNumber.trim() === "") {
        _store.update({ courtCase: undefined, courtCaseSelectionResult: null });
        return false;
      }
      if (
        courtCaseSelectionResult?.courtCase?.courtCaseNumber === courtCaseNumber
      ) {
        _store.update({
          courtCase: courtCaseSelectionResult.courtCase,
          courtCaseDetailsModalVisible: true
        });
        return true;
      }
      let _searchQuery: TSearchQueryRequest = {
        ccn: courtCaseNumber
      };
      let queryResult = await _application.container
        .resolve<RunSimpleSearchQueryInteractor>("runSimpleSearchQuery")
        .execute(_searchQuery);
      var record = await _application.container
        .resolve<FindCourtCaseDetailsInteractor>("findCourtCaseDetails")
        .execute(courtCaseNumber);
      record.hasCaseLawDocs = queryResult?.results.length > 0;
      if (record.courtCaseNumber) {
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("editors.case_law_documents.primary_cases.success")
          );
        _store.update({
          courtCaseSelectionResult: null,
          courtCase: record,
          courtCaseDetailsModalVisible: true
        });
      } else {
        _application.container
          .resolve<ShowInfoMessageInteractor>("showInfoMessage")
          .execute(
            translate("editors.case_law_documents.connected_cases.not_found")
          );

        showCreateCourtCasePopupVisible(true);
        _store.update({
          courtCase: undefined,
          courtCaseSelectionResult: null,
          courtCaseDetailsModalVisible: false,
          nonExistantCourtCaseNumber: courtCaseNumber
        });
      }
      return record !== undefined ?? false;
    };
    const createConnectedCourtCase = async (courtCase: ICourtCase) => {
      const { connectedCourtCasesList } =
        _store.getState<TCaseLawDocumentCreationPresenter>();
      let record = await _application.container
        .resolve<FindCourtCaseDetailsInteractor>("findCourtCaseDetails")
        .execute(courtCase.courtCaseNumber);
      record.type = CourtCaseType.CONNECTED;
      if (record.courtCaseNumber == null) {
        _store.update({
          connectedCourtCasesList: [...connectedCourtCasesList]
        });

        _application.container
          .resolve<ShowInfoMessageInteractor>("showInfoMessage")
          .execute(
            translate("editors.case_law_documents.connected_cases.not_found")
          );
      } else {
        _store.update({
          connectedCourtCasesList: [...connectedCourtCasesList, record]
        });
      }
    };
    const createAttachment = async (file: any, fileName?: string) => {
      if (file.status !== "removed") {
        let attachment = {
          name: fileName,
          valid: true,
          anonymised: false
        } as IAttachment;
        let record = await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(attachment, file);

        _store.update({
          uploadedAttachment: record
        });
        loader.stop("attachmentsLoader");
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("editors.project.create_success_message"));
        return record;
      } else {
        _store.update({
          uploadedAttachment: undefined
        });
        return undefined;
      }
    };
    const createCourtCase = (courtCase: any) => {
      const { connectedCourtCasesList, uploadedAttachment } =
        _store.getState<TCaseLawDocumentCreationPresenter>();
      if (courtCase?.courtCaseNumber) {
        courtCase.decisions = [
          {
            name: courtCase.caseDecisionName,
            attachmentId: uploadedAttachment?.id,
            decisionDate: courtCase.decisionDate,
            decisionTypeId: courtCase.decisionTypeId,
            caseNumber: courtCase.courtCaseNumber,
            valid: true
          }
        ];
        courtCase.type === CourtCaseType.PRIMARY
          ? _store.update({
              courtCase: courtCase,
              courtCaseDetailsModalVisible: true,
              nonExistantCourtCaseNumber: undefined
            })
          : _store.update({
              connectedCourtCasesList: [...connectedCourtCasesList, courtCase],
              courtCaseDetailsModalVisible: true,
              nonExistantCourtCaseNumber: undefined
            });
        return true;
      }
    };
    const showCreateCourtCasePopupVisible = (value: boolean) => {
      _store.update({ createCourtCasePopupVisible: value });
    };

    const showCreateCourtCaseModalVisible = (value: boolean) => {
      _store.update({ createCourtCaseModalVisible: value });
      value === false &&
        _store.update({ nonExistantCourtCaseNumber: undefined });
    };

    const removeConnectedCourtCase = async (courtCaseNumber: string) => {
      const { connectedCourtCasesList } =
        _store.getState<TCaseLawDocumentCreationPresenter>();

      var courtCases = connectedCourtCasesList.filter(
        (item) => item.courtCaseNumber !== courtCaseNumber
      );
      _store.update({
        connectedCourtCasesList: courtCases
      });
    };

    const createCaseLawDocument = async (caseLawDocument: any) => {
      loader.start("caseLawDocumentLoader");

      const { selectedLanguage } = _store.getState<any>();
      let { courtCaseSelectionResult, user } =
        _store.getState<TCaseLawDocumentCreationPresenter>();
      let courtCasesList: ICourtCase[] = [];
      if (!courtCaseSelectionResult) {
        findCourtCaseDetails(caseLawDocument?.primaryCourtCase);
        loader.stop("caseLawDocumentLoader");
        return;
      } else if (
        courtCaseSelectionResult.caseDecision &&
        !courtCaseSelectionResult.caseDecision?.organisationId &&
        user
      ) {
        courtCaseSelectionResult.caseDecision.organisationId =
          user.organisationId;
      }

      courtCasesList.push({
        courtCaseNumber: courtCaseSelectionResult?.courtCase?.courtCaseNumber,
        caseType: courtCaseSelectionResult?.courtCase?.caseType,
        decisions: courtCaseSelectionResult?.caseDecision
          ? [courtCaseSelectionResult?.caseDecision]
          : [],
        type: CourtCaseType.PRIMARY,
        beginDate: courtCaseSelectionResult?.courtCase?.beginDate,
        endDate: courtCaseSelectionResult?.courtCase?.endDate,
        caseBeginDate: courtCaseSelectionResult?.courtCase?.caseBeginDate,
        caseDescription: courtCaseSelectionResult?.courtCase?.caseDescription,
        anonymizedCaseDescription:
          courtCaseSelectionResult?.courtCase?.anonymizedCaseDescription
      } as ICourtCase);
      courtCaseSelectionResult?.relatedCases?.forEach((item) => {
        courtCasesList.push({
          courtCaseNumber: item.courtCase.courtCaseNumber,
          decisions: item.caseDecision ? [item.caseDecision] : [],
          type: CourtCaseType.CONNECTED,
          connectionTypeId: item.courtCase?.connectionTypeId
        } as ICourtCase);
      });
      let translations = caseLawDocument.summaryTranslations.map((item) => {
        var element = SummaryTranslation(item);
        element.organisationId = caseLawDocument.organisationId;
        return element;
      });

      let originalSummary = translations?.find(
        (item) => item.languageId === selectedLanguage.id
      );
      caseLawDocument.caseLawDocDate = caseLawDocument.caseLawDocDate.toDate();
      caseLawDocument.summary = originalSummary?.summaryTranslation ?? "";
      caseLawDocument.summaryTranslations = translations;
      let elementList =
        caseLawDocument.organisationIndexList &&
        caseLawDocument.organisationIndexList.length > 0
          ? caseLawDocument.organisationIndexList.flat(1)
          : [];
      let invalidatedCaseLawDocuments: IInvalidatedCaseLawDocument = {
        caseLawDocumentIdList: caseLawDocument.invalidatedCaseLawDocuments,
        invalidationReason: caseLawDocument.invalidationReason
      };
      let record = await _application.container
        .resolve<CreateCaseLawDocumentInteractor>("createCaseLawDocument")
        .execute(
          caseLawDocument,
          courtCasesList,
          elementList.filter((item) => item !== null && item !== undefined),
          invalidatedCaseLawDocuments
        );
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      loader.stop("caseLawDocumentLoader");
      showCaseLawDocumentDetails(record.id);
      _store.update({ selectedIndex: {}, selectedCourtCases: [] });
      return true;
    };

    const updateCourtCaseDetailsModalVisibility = (state: boolean) => {
      _store.update({ courtCaseDetailsModalVisible: state });
    };

    const updateCourtCaseSelection = (result: TCourtCaseSelectionModel) => {
      _store.update({
        courtCaseSelectionResult: result
      });
    };

    const showCaseLawDocumentDetails = (id: number) => {
      return _application.navigator.push({
        pathname: `/case-law-documents/${id.toString()}/details`
      });
    };
    const showCaseLawDocumentList = () => {
      return _application.navigator.push({
        pathname: `/search?q=`
      });
    };
    const onSearch = async (value: string) => {
      let { selectedOrganisationId } =
        _store.getState<TCaseLawDocumentCreationPresenter>();
      if (value.length >= 3) {
        let query: TSearchCriteriaFilter = {
          phrase: value
        };

        let _searchQuery: TSearchQueryRequest = {
          phrase: query.phrase ?? "",
          status: CaseLawDocumentStatus.PUBLISHED,
          collection: "STAV",
          organisation: selectedOrganisationId?.toString()
        };
        _store.update({
          searchQuery: _searchQuery
        });

        let result = await _application.container
          .resolve<RunSimpleSearchQueryInteractor>("runSimpleSearchQuery")
          .execute(_searchQuery);
        result &&
          _store.update({
            publishedCaseLawDocuments: result.formattedResults
          });
      }
    };

    const openIndexDataModal = async (
      index: IOrganisationIndex,
      selectedIds: number[],
      indexNr: number
    ) => {
      let { previousSelectedIds } =
        _store.getState<TCaseLawDocumentCreationPresenter>();
      let previousIds = previousSelectedIds?.[indexNr];
      if (previousIds && selectedIds.length < previousIds?.length) {
        previousSelectedIds[indexNr] = selectedIds;
        _store.update({
          previousSelectedIds
        });
        return;
      }
      if (index?.indexTypeId) {
        let indexTypeAttributes = await _application.container
          .resolve<GetAllIndexTypeAttributesInteractor>(
            "getAllIndexTypeAttributes"
          )
          .execute(index?.indexTypeId);
        let indexAttributeDataList = await _application.container
          .resolve<GetIndexAttributeDataByIndexInteractor>(
            "getIndexAttributeDataByIndex"
          )
          .execute(index?.id);
        let indexDataModalVisible = indexTypeAttributes.length > 0;
        previousSelectedIds[indexNr] = selectedIds;
        _store.update({
          indexTypeAttributes,
          indexAttributeDataList,
          indexDataModalVisible: indexDataModalVisible,
          selectedIndexForAttribute: index,
          previousSelectedIds
        });
      }
    };

    const closeIndexDataModal = () => {
      _store.update({
        indexDataModalVisible: false
      });
    };

    const setAttributesData = (
      indexTypeAttribute: IIndexTypeAttribute,
      attributeData: string
    ) => {
      loader.start("caseLawDocumentLoader");
      const { indexAttributeDataList, selectedIndexForAttribute } =
        _store.getState<TCaseLawDocumentCreationPresenter>();
      let indexAttributeData = {
        indexId: selectedIndexForAttribute?.id,
        indexTypeAttributeId: indexTypeAttribute?.id,
        attributeData: attributeData,
        valid: true
      };
      let model = IndexAttributeData(indexAttributeData);
      let existingAttributeData = indexAttributeDataList?.find(
        (item) => item.indexTypeAttributeId === model.indexTypeAttributeId
      );
      if (existingAttributeData) {
        let index = indexAttributeDataList?.indexOf(existingAttributeData);
        if (indexAttributeDataList && index !== undefined) {
          indexAttributeDataList[index].attributeData = attributeData;
        }
      } else {
        indexAttributeDataList?.push(model);
      }
      let filteredList = indexAttributeDataList?.filter(
        (d) => d.attributeData && d.attributeData !== ""
      );
      _store.update({
        indexAttributeData: model,
        indexAttributeDataList: filteredList
      });
      loader.stop("caseLawDocumentLoader");
    };

    const saveAttributeData = async () => {
      loader.start("caseLawDocumentLoader");
      const { indexAttributeDataList, indexTypeAttributes } =
        _store.getState<TCaseLawDocumentCreationPresenter>();
      let invalidTypes: IIndexTypeAttribute[] = [];
      indexTypeAttributes?.forEach(async (type) => {
        let existingData = indexAttributeDataList?.filter(
          (d) => d.indexTypeAttributeId === type.id && d.attributeData
        );
        if (existingData?.length === 0 && type.required) {
          invalidTypes.push(type);
        }
      });
      if (invalidTypes.length > 0) {
        await _application.container
          .resolve<ShowWarningMessageInteractor>("showWarningMessage")
          .execute(translate("editors.case_law_documents.required_data"));
        loader.stop("indexTypeLoader");
        return;
      }
      indexAttributeDataList &&
        (await _application.container
          .resolve<CreateIndexAttributeDataInteractor>(
            "createIndexAttributeData"
          )
          .execute(indexAttributeDataList));
      closeIndexDataModal();
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      _store.update({ indexAttributeDataList: [] });
      loader.stop("caseLawDocumentLoader");
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadReferenceValues,
      setOrganisationId,
      createCaseLawDocument,
      showCaseLawDocumentList,
      createConnectedCourtCase,
      removeConnectedCourtCase,
      findCourtCaseDetails,
      setCaseLawDocumentTypeCode,
      setParentGroupId,
      setSecondGroupId,
      setInvalidatePublishedCaseLawDocuments,
      onSearch,
      updateCourtCaseDetailsModalVisibility,
      updateCourtCaseSelection,
      showCreateCourtCasePopupVisible,
      showCreateCourtCaseModalVisible,
      createCourtCase,
      createAttachment,
      setAttributesData,
      openIndexDataModal,
      closeIndexDataModal,
      saveAttributeData
    };
  },
  defaultState
);
export default CaseLawDocumentCreationPresenter;
